import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../core/auth";
import Partition from "../../components/common/partition";
import { track } from "@amplitude/analytics-browser";
import AppDownloadModal from "../../components/app-download/app-download-modal";
import { isWeb } from "../../utils/user-agent";
import { getExposeType } from "../../utils/store-expose";
import { getCategoryName } from "../store/utils/store-category";
import LoginBottomSheet from "../../components/common/login-bottom-sheet";
import ReserveConfirmBottomSheet from "../store/components/reserve-confirm-bottom-sheet";
import {
  calculateReserveTimeBefore30Minutes,
  getReserveTimeRaw,
  getReserveTimeForTomorrowRaw,
} from "./utils/pack-reserve-time";
import { usePackPageData } from "./hooks/use-pack-page-data";
import PackHeader from "./components/pack-header";
import PackPickupInfo from "./components/pack-pickup-info";
import PackDescription from "./components/pack-description";
import PackRemainingCount from "./components/pack-remaining-count";
import PackPrice from "./components/pack-price";
import PackReviews from "./components/pack-reviews";
import PackReserveButton from "./components/pack-reserve-button";
import ReviewDetailModal from "./components/review-detail-modal";
import type { Review } from "../../__generated__/graphql";

const Pack = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { storeId } = useParams();
  const { pack, store, loading, packReviews } = usePackPageData(storeId);

  const quantity = 1;
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoginSheetOpen, setIsLoginSheetOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | undefined>();

  const exposeType = getExposeType(pack);
  const categoryName = getCategoryName(pack?.category);

  const isPackSoldOut =
    pack?.currentCount === undefined ||
    pack?.currentCount === null ||
    pack?.currentCount <= 0;

  const titleSuffix =
    (exposeType === "내일" ? "내일" : "오늘") + "의 " + categoryName;

  const handleReserveClick = () => {
    track("팩 예약 버튼 클릭", {
      매장명: store?.title,
      가격: pack?.userPrice,
      수량: quantity,
    });

    if (isWeb()) {
      setShowAppDownloadModal(true);
      return;
    }

    if (!isLoggedIn) {
      setIsLoginSheetOpen(true);
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirm = (isTomorrow: boolean) => {
    setShowConfirmModal(false);

    const reserveTime = store?.reserveTime as
      | Record<string, { start?: string; end?: string }>
      | null
      | undefined;
    const time = calculateReserveTimeBefore30Minutes(
      reserveTime,
      isTomorrow ? 1 : 0
    );
    const reserveTimeStr = isTomorrow
      ? getReserveTimeForTomorrowRaw(reserveTime)
      : getReserveTimeRaw(reserveTime);

    navigate(
      `/pre-order/intro/${storeId}?time=${time}&isTomorrow=${isTomorrow}&reserveTime=${reserveTimeStr}`
    );
  };

  const packContainerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    packContainerRef.current?.scrollTo(0, 0);
  }, [loading]);

  const discountAmount = (pack?.price ?? 0) - (pack?.userPrice ?? 0);

  return (
    <div
      className="container h-screen flex flex-col mx-auto bg-[#F4F5F7] overflow-hidden"
      ref={packContainerRef}
    >
      <div className="flex-1 overflow-y-auto pb-12">
        <div className="relative w-full mx-auto bg-white">
          <PackHeader
            storeImg={store?.img}
            storeTitle={store?.title}
            titleSuffix={titleSuffix}
            loading={loading}
          />

          {!loading && pack && (
            <div className="px-4 pb-3">
              <PackPickupInfo
                reserveTime={store?.reserveTime as never}
                exposeType={exposeType}
              />
              <PackDescription description={pack.description} />
            </div>
          )}
        </div>

        <Partition color="light" height="thick" />

        <PackRemainingCount
          currentCount={pack?.currentCount}
          loading={loading}
        />

        <Partition color="light" height="md" />

        <PackPrice
          price={pack?.price}
          userPrice={pack?.userPrice}
          loading={loading}
        />

        <Partition color="light" height="md" />

        {packReviews.length > 0 && (
          <>
            <PackReviews
              reviews={packReviews}
              onReviewClick={setSelectedReview}
            />
            <Partition color="light" height="thick" />
          </>
        )}
      </div>

      {!isPackSoldOut && exposeType !== "X" && (
      <PackReserveButton
        quantity={quantity}
        userPrice={pack?.userPrice}
        discountAmount={discountAmount}
        loading={loading}
        onClick={handleReserveClick}
      />
    )}

    <ReserveConfirmBottomSheet
      isOpen={showConfirmModal}
      onClose={() => setShowConfirmModal(false)}
      onConfirm={handleConfirm}
      exposeType={exposeType}
      store={store}
    />

    <LoginBottomSheet
      isOpen={isLoginSheetOpen}
      onClose={() => setIsLoginSheetOpen(false)}
      discountRate={pack?.discountRate || 0}
    />

    {selectedReview && (
      <ReviewDetailModal
        review={selectedReview}
        onClose={() => setSelectedReview(undefined)}
      />
    )}

    <AppDownloadModal
      isOpen={showAppDownloadModal}
      onClose={() => setShowAppDownloadModal(false)}
      where="팩 상세 페이지"
    />
    </div>
  );
};

export default Pack;
